if ( typeof GreetingCardPicker !== 'function' ) {

	/*
	 * Drives the greeting card upsell rendered by snippets/greeting-card-picker.liquid.
	 *
	 * The card is one product with one variant, so there is nothing to choose: this
	 * only decides whether a card is wanted, collects its message, and adds it to the
	 * cart as its own line after the product it belongs to.
	 */
	class GreetingCardPicker extends HTMLElement {

		constructor(){

			super();

			this.o = {
				...{
					maxLength: 200,
					requireMessage: false,
					messageProperty: 'Meddelande',
					sourceProduct: ''
				}, ...JSON.parse(this.dataset.options)
			};

			this.init();

		}

		init(){

			this.productPage = this.closest('[data-js-product-component]');
			this.checkbox = this.querySelector('[data-js-greeting-card-toggle]');
			this.panel = this.querySelector('[data-js-greeting-card-panel]');
			this.messageElm = this.querySelector('[data-js-greeting-card-message]');
			this.counterElm = this.querySelector('[data-js-greeting-card-counter]');
			this.errorElm = this.querySelector('[data-js-greeting-card-error]');
			this.addErrorElm = this.querySelector('[data-js-greeting-card-add-error]');

			if ( ! this.productPage || ! this.checkbox || ! this.panel ) return;

			this.productForm = this.productPage.querySelector('product-form');

			// no-overlay carts redirect to /cart before the theme dispatches its
			// `add-to-cart` event, so those need the fallback path in _onSubmit()
			this.pageMode = ! this.productForm || ! this.productForm.hasAttribute('data-ajax-cart');

			this.checkbox.addEventListener('change', () => this._onToggle(true));
			this._onToggle(false);

			if ( this.messageElm ) {
				this.messageElm.addEventListener('input', this._onMessageInput.bind(this));
				this._onMessageInput();
			}

			// bound in the capture phase, on an ancestor of the form, so this always
			// runs before the theme's own ProductForm submit handler
			this.productPage.addEventListener('submit', this._onSubmit.bind(this), true);

			if ( this.productForm ) {
				this.productForm.addEventListener('add-to-cart', this._onAddToCart.bind(this));
			}

		}

		get variantId(){
			return parseInt(this.dataset.variantId);
		}

		get message(){
			return this.messageElm ? this.messageElm.value.trim() : '';
		}

		get isActive(){
			return this.checkbox.checked;
		}

		_onToggle(focusMessage = true){

			this.panel.hidden = ! this.checkbox.checked;
			this.classList.toggle('greeting-card--open', this.checkbox.checked);

			if ( ! this.checkbox.checked ) {
				this._toggleError(false);
			} else if ( focusMessage && this.messageElm ) {
				this.messageElm.focus({ preventScroll: true });
			}

		}

		_onMessageInput(){

			if ( this.counterElm ) {
				this.counterElm.textContent = this.messageElm.value.length;
			}

			if ( this.message !== '' ) {
				this._toggleError(false);
			}

		}

		_toggleError(show){
			if ( this.errorElm ) {
				this.errorElm.style.display = show ? 'block' : 'none';
			}
		}

		_toggleAddError(show){
			if ( this.addErrorElm ) {
				this.addErrorElm.style.display = show ? 'block' : 'none';
			}
		}

		// the card failing to reach the cart is invisible otherwise: the product is
		// already in, the drawer is open, and nothing says the card was dropped
		_onAddFailed(error){

			console.group('[greeting-card] the card could not be added to the cart');
			console.log('Shopify said :', error && error.message ? error.message : error);
			console.log('HTTP status  :', error && error.httpStatus ? error.httpStatus : '(request never completed)');
			console.log('endpoint     :', `${KROWN.settings.routes.cart_add_url}.js`);
			console.log('sent         :', this._lastPayload);
			console.log('variant id   :', this.dataset.variantId || '(none rendered)');
			console.log('raw response :', error && error.body ? error.body : '(none)');
			console.groupEnd();

			this.checkbox.checked = true;
			this._onToggle(false);
			this._toggleAddError(true);

		}

		_onSubmit(e){

			if ( this._passthrough || ! this.isActive ) return;

			if ( this.o.requireMessage && this.message === '' ) {
				e.preventDefault();
				e.stopImmediatePropagation();
				this._toggleError(true);
				this.messageElm.focus();
				return;
			}

			if ( ! this.pageMode ) return;

			e.preventDefault();
			e.stopImmediatePropagation();

			const form = e.target;
			const submitButton = form.querySelector('[type="submit"]');

			if ( submitButton ) submitButton.classList.add('working');

			// the product is added first: if it fails (sold out, for instance) we must
			// not end up with an orphan greeting card sitting in the cart
			fetch(`${KROWN.settings.routes.cart_add_url}.js`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
					'X-Requested-With': 'XMLHttpRequest'
				},
				body: new URLSearchParams(new FormData(form)).toString()
			})
				.then(response => response.json())
				.then(response => {
					if ( response.status ) throw new Error(response.description || response.message);
					return this._addCard();
				})
				.then(() => {
					document.location.href = KROWN.settings.routes.cart_url;
				})
				.catch(e => {
					this._onAddFailed(e);
					if ( submitButton ) submitButton.classList.remove('working');
				});

		}

		_onAddToCart(){

			if ( ! this.isActive ) return;

			this._toggleAddError(false);

			// the reset and the refresh live here, not inside _addCard(), so that a
			// failure to repaint the cart drawer is never reported as a failure to add
			this._addCard()
				.then(() => {
					this._reset();
					return this._refreshCart();
				})
				.catch(e => this._onAddFailed(e));

		}

		_addCard(){

			if ( ! this.variantId ) return Promise.resolve();

			const properties = {};

			if ( this.message !== '' ) {
				properties[this.o.messageProperty] = this.message;
			}

			// underscore-prefixed properties stay hidden from the customer but are
			// visible on the order in the Shopify admin
			if ( this.o.sourceProduct !== '' ) {
				properties['_for_product'] = this.o.sourceProduct;
			}

			this._lastPayload = {
				items: [{
					id: this.variantId,
					quantity: 1,
					properties: properties
				}]
			};

			return fetch(`${KROWN.settings.routes.cart_add_url}.js`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Accept': 'application/json',
					'X-Requested-With': 'XMLHttpRequest'
				},
				body: JSON.stringify(this._lastPayload)
			})
				// read as text first: fetch does not reject on 4xx/5xx, and a rejected
				// request is not always JSON (a password page or a Shopify block page
				// comes back as HTML, which would otherwise surface as a parse error)
				.then(response => response.text().then(text => ({ response, text })))
				.then(({ response, text }) => {

					let data = null;
					try { data = JSON.parse(text); } catch (e) { /* not JSON */ }

					if ( ! response.ok || ( data && data.status ) ) {
						const error = new Error(
							( data && ( data.description || data.message ) ) || `HTTP ${response.status} ${response.statusText}`
						);
						error.httpStatus = response.status;
						error.body = text.slice(0, 600);
						throw error;
					}

					return data;

				});

		}

		_reset(){

			this.checkbox.checked = false;
			if ( this.messageElm ) this.messageElm.value = '';
			this._onToggle(false);
			this._toggleAddError(false);
			if ( this.messageElm ) this._onMessageInput();

		}

		_refreshCart(){

			return fetch('?section_id=helper-cart')
				.then(response => response.text())
				.then(text => {

					const section = new DOMParser().parseFromString(text, 'text/html');

					const cartItems = document.getElementById('AjaxCartForm');
					if ( cartItems && section.getElementById('AjaxCartForm') ) {
						cartItems.innerHTML = section.getElementById('AjaxCartForm').innerHTML;
						cartItems.ajaxifyCartItems();
					}

					const cartSubtotal = document.getElementById('AjaxCartSubtotal');
					if ( cartSubtotal && section.getElementById('AjaxCartSubtotal') ) {
						cartSubtotal.innerHTML = section.getElementById('AjaxCartSubtotal').innerHTML;
					}

					if ( section.querySelector('[data-cart-count]') ) {
						document.querySelectorAll('[data-header-cart-count]').forEach(elm=>{
							elm.textContent = section.querySelector('[data-cart-count]').textContent;
						});
					}

					if ( section.querySelector('[data-cart-total]') ) {
						document.querySelectorAll('[data-header-cart-total]').forEach(elm=>{
							elm.textContent = section.querySelector('[data-cart-total]').textContent;
						});
					}

				})
				// the card is already in the cart by this point, so a repaint failure
				// must stay a console problem and never become a customer-facing error
				.catch(e => {
					console.log('[greeting-card] the card was added but the cart drawer could not be refreshed', e);
				});

		}

	}

	customElements.define('greeting-card-picker', GreetingCardPicker);

}
